// src/pages/Instrument_Runs/components/RunEditForm/RunEditForm.tsx
import { instrumentRunQueryKeys } from '@/config/queryKeys';
import { useApiClient } from '@/hooks/useApiClient';
import { applyInstrumentRunsListRefresh } from '@/lib/instrumentRunsRefresh';
import SampleManager from '@/pages/Instrument_Runs/components/SampleManager/SampleManager';
import { useAppStore } from '@/stores/app';
import type {
  InstrumentRunAssayVersion,
  InstrumentRunConsumables as InstrumentRunConsumablesType,
  InstrumentRunDataRow,
  InstrumentRunDraftCreate,
  InstrumentRunDraftUpdate,
  InstrumentRunForm,
  InstrumentRunFormValues,
  InstrumentRunPublic,
  InstrumentRunSampleSize,
  InstrumentRunSpecies,
  Org,
  StudyPublicGetMany,
} from '@/types';
import { INSTRUMENT_RUN_ASSAY_VERSIONS } from '@/types';
import { Button } from '@nautilusbio/nb-design-system/Button';
import { Grid } from '@nautilusbio/nb-design-system/Grid';
import { Input } from '@nautilusbio/nb-design-system/Input';
import { Select } from '@nautilusbio/nb-design-system/Select';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Textarea } from '@nautilusbio/nb-design-system/Textarea';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import { useForm, useStore } from '@tanstack/react-form';
import { useQueryClient } from '@tanstack/react-query';
import { useNavigate } from '@tanstack/react-router';
import { CloudUploadIcon, SaveIcon, XIcon } from 'lucide-react';
import React, { forwardRef, useCallback, useImperativeHandle, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import z from 'zod';
import { useShallow } from 'zustand/react/shallow';

import irStyles from '../../styles.module.css';
import InstrumentRunConsumables from '../InstrumentRunConsumables';

import ControlsSection from './ControlsSection';
import styles from './styles.module.css';

const EDIT_READONLY_COLUMNS = ['flow_cell', 'lane', 'well_position', 'demux_index', 'org_name'];
const ALLOWED_SPECIES = ['Human', 'Mouse'] as const;
const NO_OF_SAMPLES_OPTIONS = ['4', '12', '24', '36'] as const;
const BUTTON_ICON_SIZE = 18;

export type RunEditFormHandle = {
  submit: () => void;
  saveDraft: () => void;
  submitDraft: () => void;
  form: InstrumentRunForm;
};

export type RunEditFormProps = {
  mode: 'create' | 'edit';
  run?: InstrumentRunPublic;
  orgId: string;
  organizations: Org[];
  studies: StudyPublicGetMany[];
  showHeader?: boolean;
  onSave: () => void;
  onCancel?: () => void;
};

const RunEditForm = forwardRef<RunEditFormHandle, RunEditFormProps>(function RunEditForm(
  { mode, run, orgId, organizations, showHeader, onSave, onCancel },
  ref,
) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const api = useApiClient();
  const queryClient = useQueryClient();

  const setShouldRefreshInstrumentRuns = useAppStore(useShallow(state => state.setShouldRefreshInstrumentRuns));

  const orgNameToId = useMemo(() => {
    const map = new Map<string, string>();
    for (const org of organizations) map.set(org.name, String(org.id));
    return map;
  }, [organizations]);

  const orgIdToName = useMemo(() => {
    const map = new Map<string, string>();
    for (const org of organizations) map.set(String(org.id), org.name);
    return map;
  }, [organizations]);

  const initialValues = useMemo((): InstrumentRunFormValues => {
    if (mode === 'edit' && run) {
      const samples = (run.samples ?? [])
        .filter(s => !s.is_control)
        .map(s => ({
          name: s.name ?? '',
          study_name: s.study_name ?? '',
          org_name: s.org_name ?? (s.org_id ? (orgIdToName.get(String(s.org_id)) ?? '') : ''),
          org_id: s.org_id ?? '',
          well_position: s.well_position ?? '',
          demux_index: s.demux_index ?? 0,
          is_control: s.is_control ?? false,
          control_type: s.control_type ?? '',
          flow_cell: (s as InstrumentRunDataRow).flow_cell ?? null,
          lane: (s as InstrumentRunDataRow).lane ?? null,
          library_group: (s as InstrumentRunDataRow).library_group ?? null,
          sample_group: (s as InstrumentRunDataRow).sample_group ?? null,
        }));

      return {
        name: run.name ?? '',
        description: run.description ?? '',
        species: (run.species ?? 'Human') as InstrumentRunSpecies,
        noOfSamples: String(run.num_samples ?? 12),
        assay_version: (run.assay_version ?? INSTRUMENT_RUN_ASSAY_VERSIONS[0]) as InstrumentRunAssayVersion,
        samples,
        useTauLibPrepControl2: false,
        consumables: run.consumables ?? {},
      };
    }
    return {
      name: '',
      description: '',
      species: 'Human',
      noOfSamples: '12',
      assay_version: INSTRUMENT_RUN_ASSAY_VERSIONS[0],
      samples: [],
      useTauLibPrepControl2: false,
      consumables: {},
    };
  }, [mode, run, orgIdToName]);

  const schema = useMemo(
    () =>
      z.object({
        name: z
          .string()
          .min(1, t('create:run.validation.nameRequired'))
          .refine(v => !/['`]/.test(v), {
            message: t('create:run.validation.nameInvalidChars'),
          }),
        description: z.string().optional(),
        species: z.enum(ALLOWED_SPECIES),
        assay_version: z.enum(
          INSTRUMENT_RUN_ASSAY_VERSIONS as [InstrumentRunAssayVersion, ...InstrumentRunAssayVersion[]],
        ),
        noOfSamples: z.string().min(1),
        samples: z.array(
          z.object({
            name: z.string(),
            study_name: z.string(),
            org_name: z.string().optional(),
            well_position: z.string(),
            demux_index: z.number(),
            is_control: z.boolean().optional(),
            control_type: z.string().optional(),
            flow_cell: z.string().nullable().optional(),
            lane: z.number().nullable().optional(),
            library_group: z.string().nullable().optional(),
            sample_group: z.string().nullable().optional(),
          }),
        ),
        useTauLibPrepControl2: z.boolean().optional(),
        consumables: z.record(z.string(), z.string().nullable().optional()).optional(),
      }),
    [t],
  );

  const form = useForm({
    defaultValues: initialValues,
    validators: {
      onChange: ({ value }) => {
        const result = schema.safeParse(value);
        return result.success ? undefined : result.error.message;
      },
    },
    onSubmit: async ({ value }) => {
      await handleSubmit(value);
    },
  });

  const buildPayload = useCallback(
    (value: InstrumentRunFormValues) => ({
      assay_version: value.assay_version,
      name: value.name,
      description: value.description ?? null,
      species: value.species as InstrumentRunSpecies,
      num_samples: Number(value.noOfSamples) as InstrumentRunSampleSize,
      samples: value.samples.map(s => {
        const resolvedOrgId = s.org_id || (s.org_name ? (orgNameToId.get(s.org_name) ?? null) : null);
        return {
          name: s.name || null,
          study_name: s.study_name || null,
          org_id: resolvedOrgId,
          org_name: resolvedOrgId ? null : (s.org_name ?? null),
          well_position: s.well_position || null,
          demux_index: s.demux_index,
          is_control: s.is_control ?? null,
          control_type: s.control_type ?? null,
          flow_cell: s.flow_cell ?? null,
          lane: s.lane ?? null,
          library_group: s.library_group ?? null,
          sample_group: s.sample_group ?? null,
        };
      }),
    }),
    [orgNameToId],
  );

  const handleSaveDraft = useCallback(
    async (value: InstrumentRunFormValues) => {
      if (!value.name) {
        toast.error(t('create:run.validation.nameRequired'));
        return;
      }
      try {
        const payload: InstrumentRunDraftCreate = { ...buildPayload(value) };
        const created = await api.post<InstrumentRunPublic>(instrumentRunQueryKeys.endpoints.draft(orgId), payload);
        if (!created) return;
        await applyInstrumentRunsListRefresh(orgId, queryClient, setShouldRefreshInstrumentRuns);
        toast.success(`${value.name} ${t('create:run.created')}`);
        void navigate({ to: '/instrument_runs/$instrumentRunId', params: { instrumentRunId: created.id } });
      } catch (error) {
        if (import.meta.env.DEV) console.error(error);
        toast.error(t('create:run.failedToSaveDraft'));
      }
    },
    [t, api, orgId, queryClient, setShouldRefreshInstrumentRuns, buildPayload, navigate],
  );

  const handleSubmit = useCallback(
    async (value: InstrumentRunFormValues) => {
      if (!value.name) {
        toast.error(t('create:run.validation.nameRequired'));
        return;
      }
      try {
        const draftPayload: InstrumentRunDraftCreate = {
          ...buildPayload(value),
        };
        const draft = await api.post<InstrumentRunPublic>(instrumentRunQueryKeys.endpoints.draft(orgId), draftPayload);
        if (!draft) return;
        await api.post(instrumentRunQueryKeys.endpoints.submit(orgId, draft.id), {});
        await applyInstrumentRunsListRefresh(orgId, queryClient, setShouldRefreshInstrumentRuns);
        toast.success(`${value.name} ${t('create:run.created')}`);
        void navigate({
          to: '/instrument_runs/$instrumentRunId',
          params: { instrumentRunId: draft.id },
        });
        onSave();
      } catch (error) {
        if (import.meta.env.DEV) console.error(error);
        toast.error(t('create:run.failed'));
      }
    },
    [t, api, orgId, queryClient, setShouldRefreshInstrumentRuns, navigate, onSave, buildPayload],
  );

  const handleSaveEdit = useCallback(
    async (value: InstrumentRunFormValues) => {
      if (!run) return;
      if (!value.name) {
        toast.error(t('create:run.validation.nameRequired'));
        return;
      }
      try {
        const payload: InstrumentRunDraftUpdate = { ...buildPayload(value) };
        const updated = await api.put<InstrumentRunPublic>(
          instrumentRunQueryKeys.endpoints.detail(orgId, run.id),
          payload,
        );
        if (!updated) return;
        await queryClient.invalidateQueries({
          queryKey: instrumentRunQueryKeys.detail(orgId, run.id),
        });
        await applyInstrumentRunsListRefresh(orgId, queryClient, setShouldRefreshInstrumentRuns);
        toast.success(`${value.name} ${t('create:run.updated')}`);
        onSave();
      } catch (error) {
        if (import.meta.env.DEV) console.error(error);
        toast.error(t('create:run.failed'));
      }
    },
    [run, t, api, orgId, queryClient, setShouldRefreshInstrumentRuns, onSave, buildPayload],
  );

  const handleSubmitDraft = useCallback(
    async (value: InstrumentRunFormValues) => {
      if (!run) return;
      if (!value.name) {
        toast.error(t('create:run.validation.nameRequired'));
        return;
      }
      try {
        const payload: InstrumentRunDraftUpdate = { ...buildPayload(value) };
        const updated = await api.put<InstrumentRunPublic>(
          instrumentRunQueryKeys.endpoints.detail(orgId, run.id),
          payload,
        );
        if (!updated) return;
        await api.post(instrumentRunQueryKeys.endpoints.submit(orgId, run.id), {});
        await queryClient.invalidateQueries({
          queryKey: instrumentRunQueryKeys.detail(orgId, run.id),
        });
        await applyInstrumentRunsListRefresh(orgId, queryClient, setShouldRefreshInstrumentRuns);
        toast.success(`${value.name} ${t('create:run.submitted')}`);
        void navigate({ to: '/instrument_runs/$instrumentRunId', params: { instrumentRunId: run.id } });
      } catch (error) {
        if (import.meta.env.DEV) console.error(error);
        toast.error(t('create:run.failed'));
      }
    },
    [run, t, api, orgId, queryClient, setShouldRefreshInstrumentRuns, buildPayload, navigate],
  );

  useImperativeHandle(
    ref,
    () => ({
      submit: () => {
        if (mode === 'edit') void handleSaveEdit(form.state.values);
        else void form.handleSubmit();
      },
      saveDraft: () => {
        if (mode === 'edit') void handleSaveEdit(form.state.values);
        else void handleSaveDraft(form.state.values);
      },
      submitDraft: () => void handleSubmitDraft(form.state.values),
      form: form as unknown as InstrumentRunForm,
    }),
    [mode, form, handleSaveEdit, handleSaveDraft, handleSubmitDraft],
  );

  const noOfSamplesStr = useStore(form.store, s => s.values.noOfSamples);
  const noOfSamples = Number(noOfSamplesStr ?? 12);
  const assayVersion = useStore(form.store, s => s.values.assay_version);
  const useTauLibPrepControl2 = useStore(form.store, s => s.values.useTauLibPrepControl2 ?? false);
  const flowcellSns = run?.consumables?.three_flow_cell_ids?.split(',').map(s => s.trim());

  return (
    <Stack className={styles.container} fullWidth gap={4} orientation="vertical">
      {showHeader && (
        <Stack className={irStyles.headerCard} fullWidth orientation="vertical">
          {/* Name + Assay Version */}
          <Grid
            alignItems="center"
            className={irStyles.headerTitleRow}
            fullWidth
            gap={6}
            gridTemplateColumns="1fr .2fr"
          >
            <form.Field name="name">
              {field => (
                <Input
                  data-testid="run-name-input"
                  fullWidth
                  id="run-name"
                  label={{
                    children: t('header.name', { ns: 'runs' }),
                    htmlFor: 'run-name',
                    id: 'label-run-name',
                  }}
                  onChange={e => field.handleChange((e.target as HTMLInputElement).value)}
                  value={(field.state.value as string) ?? ''}
                />
              )}
            </form.Field>
            <form.Field name="assay_version">
              {field => (
                <Select
                  allowClear={false}
                  classNames={{
                    button: irStyles.headerSelect,
                    container: irStyles.assaySelect,
                  }}
                  data-testid="select-assay-version"
                  id="assay-version"
                  label={{
                    children: t('header.assay_version', { ns: 'runs' }),
                    id: 'label-assay-version',
                  }}
                  onChange={idx => {
                    const val = INSTRUMENT_RUN_ASSAY_VERSIONS[idx];
                    if (val !== undefined) field.handleChange(val);
                  }}
                  options={[...INSTRUMENT_RUN_ASSAY_VERSIONS]}
                  value={INSTRUMENT_RUN_ASSAY_VERSIONS.indexOf(field.state.value as InstrumentRunAssayVersion)}
                />
              )}
            </form.Field>
          </Grid>

          {/* Description */}
          <form.Field name="description">
            {field => (
              <Textarea
                data-testid="run-description-textarea"
                label={{
                  children: t('header.description', { ns: 'runs' }),
                  htmlFor: 'run-description',
                  id: 'label-run-description',
                }}
                onChange={e => field.handleChange((e.target as HTMLTextAreaElement).value)}
                value={(field.state.value as string) ?? ''}
              />
            )}
          </form.Field>

          {/* Samples + Species — 2 items each at 1/3 width, start-aligned */}
          <div className={irStyles.headerSelectsRow}>
            <div className={irStyles.headerSelectItem}>
              <form.Field name="noOfSamples">
                {field => (
                  <Select
                    allowClear={false}
                    classNames={{ button: irStyles.headerSelect }}
                    data-testid="select-no-of-samples"
                    id="no-of-samples"
                    label={{
                      children: t('header.samples', { ns: 'runs' }),
                      id: 'label-no-of-samples',
                    }}
                    onChange={idx => {
                      const val = NO_OF_SAMPLES_OPTIONS[idx];
                      if (val !== undefined) field.handleChange(val);
                    }}
                    options={[...NO_OF_SAMPLES_OPTIONS]}
                    value={NO_OF_SAMPLES_OPTIONS.indexOf(
                      (field.state.value as (typeof NO_OF_SAMPLES_OPTIONS)[number]) ?? '4',
                    )}
                  />
                )}
              </form.Field>
            </div>
            <div className={irStyles.headerSelectItem}>
              <form.Field name="species">
                {field => (
                  <Select
                    allowClear={false}
                    classNames={{ button: irStyles.headerSelect }}
                    data-testid="select-species"
                    id="species"
                    label={{
                      children: t('header.species', { ns: 'runs' }),
                      id: 'label-species',
                    }}
                    onChange={idx => {
                      const val = ALLOWED_SPECIES[idx];
                      if (val !== undefined) field.handleChange(val);
                    }}
                    options={[...ALLOWED_SPECIES]}
                    value={ALLOWED_SPECIES.indexOf(field.state.value as (typeof ALLOWED_SPECIES)[number])}
                  />
                )}
              </form.Field>
            </div>
          </div>
        </Stack>
      )}

      {/* Controls — display only, computed from assay version + sample count */}
      <ControlsSection
        assayVersion={assayVersion}
        flowcellSns={flowcellSns}
        numSamples={noOfSamples}
        useTauLibPrepControl2={useTauLibPrepControl2}
      />

      {/* Samples — non-control rows only */}
      <SampleManager
        form={form as unknown as InstrumentRunForm}
        noOfSamples={noOfSamples}
        readOnlyColumns={mode === 'edit' ? EDIT_READONLY_COLUMNS : []}
        showCopyToRows={mode === 'edit'}
      />

      {/* Consumables — read-only; backend draft schema does not accept consumables */}
      <InstrumentRunConsumables
        isEditing={false}
        values={form.state.values.consumables as Partial<InstrumentRunConsumablesType>}
      />

      {/* Footer — only shown when buttons are not hosted in a parent action row */}
      {!showHeader && (
        <Stack className={styles.footer} fullWidth gap={3} justifyContent="end" orientation="horizontal">
          {onCancel && (
            <Button data-testid="run-edit-cancel" onPress={onCancel} visual="outline">
              <XIcon size={BUTTON_ICON_SIZE} />
              {t('cancel', { ns: 'general' })}
            </Button>
          )}

          {mode === 'create' && (
            <Button
              data-testid="run-save-draft"
              onPress={() => void handleSaveDraft(form.state.values)}
              visual="outline"
            >
              <SaveIcon size={BUTTON_ICON_SIZE} />
              {t('create:run.saveAsDraft')}
            </Button>
          )}

          {mode === 'edit' && run?.status === 'DRAFT' && (
            <Button
              data-testid="run-save-draft"
              onPress={() => void handleSaveEdit(form.state.values)}
              visual="outline"
            >
              <SaveIcon size={BUTTON_ICON_SIZE} />
              {t('create:run.saveAsDraft')}
            </Button>
          )}

          <Button
            data-testid={mode === 'edit' ? 'run-save' : 'run-submit'}
            onPress={() => {
              if (mode === 'edit') {
                void handleSaveEdit(form.state.values);
              } else {
                void form.handleSubmit();
              }
            }}
            visual="primary"
          >
            <CloudUploadIcon size={BUTTON_ICON_SIZE} />
            {mode === 'edit' ? t('save', { ns: 'general' }) : t('create:run.submit', { defaultValue: 'Submit' })}
          </Button>
        </Stack>
      )}
    </Stack>
  );
});

RunEditForm.displayName = 'RunEditForm';

export default RunEditForm;
